import { Button, Chip, Link } from "@mui/material";
import { CustomMarkdown, SummaryAndDetails } from "./AiChatSingleThreaded";
import { JsonNavigator } from "./JsonNavigator";
import {
    ElicitationResponseBool,
    ElicitationResponseTemplateBidRadios,
} from "./AiElicitationResponseWidgets";
import ArtifactRenderer from "./ArtifactRenderer";
import { tryParseJson } from "@/utils";
import { request } from "@/Api";
import { useState } from "react";

export const processMessageData = (data, sendMessage, resumeThread) => {
    try {
        const type = data.type;
        const role = data.role;
        const contents = data.content;
        const threadId = data.thread_id;
        const promptTemplate = data.prompt_template;

        if (typeof data == "string") {
            return data;
        }
        if (typeof contents == "string") {
            return handleTextData(role, contents);
        }
        if (type.startsWith("prompt:")) {
            return handlePromptData(role, type);
        }
        if (type == "file") {
            return handleFile(role, data);
        }
        if (promptTemplate) {
            return <b>{promptTemplate}</b>;
        }

        const blocks = [];
        contents.forEach((content) => {
            if (type == "text") {
                blocks.push(handleObjectData(role, content));
            } else if (type == "elicitation_request") {
                blocks.push(
                    handleElicitationRequest(
                        role,
                        content,
                        threadId,
                        resumeThread,
                    ),
                );
            } else if (type == "elicitation_response") {
                blocks.push(handleElicitationResponse(role, content));
            } else if (type == "tool_use") {
                blocks.push(handleToolCall(role, content));
            } else if (type == "tool_result") {
                blocks.push(handleToolCallResult(role, content, sendMessage));
            }
        });
        const filteredBlocks = blocks.filter(Boolean);
        if (filteredBlocks.length === 0) return null;
        return (
            <>
                {filteredBlocks.map((block, index) => (
                    <div key={index}>{block}</div>
                ))}
            </>
        );
    } catch (error) {
        console.error(error, data);
        return (
            <span>Error processing message data {JSON.stringify(data)}</span>
        );
    }
};

const handleTextData = (role, content) => {
    return <CustomMarkdown>{content}</CustomMarkdown>;
};

const handlePromptData = (role, type) => {
    const prompt = type.split(":")[1];
    const promptSummaryMapping = {
        create_project_brief: "Create a project brief",
    };
    const promptSummary = promptSummaryMapping[prompt];
    if (promptSummary) {
        return <i>{promptSummary}</i>;
    }
};

const handleObjectData = (role, contentObj) => {
    if (contentObj.type == "text") {
        return <CustomMarkdown>{contentObj.text}</CustomMarkdown>;
    }
    return (
        <SummaryAndDetails
            detailsElement={
                <JsonNavigator
                    data={contentObj}
                    name={contentObj.name || "Response"}
                    level={0}
                    md_keys={["document_md", "content"]}
                />
            }
            summaryElement={<span>{"Tool call result"}</span>}
        />
    );
};

const handleToolCallResult = (role, content, sendMessage) => {
    const resultTextObj = tryParseJson(content.content);

    if (content.is_error) {
        console.log(content);
        return handleErrorResult(role, content);
    }

    if (resultTextObj) {
        if (resultTextObj.type == "bid") {
            return handleBidResult(role, resultTextObj.data);
        } else if (resultTextObj.type == "client") {
            return handleClientResult(role, resultTextObj.data);
        } else if (resultTextObj.type == "project") {
            return handleProjectResult(role, resultTextObj.data);
        } else if (resultTextObj.type == "document") {
            return handleDocument(
                role,
                resultTextObj.data.name,
                resultTextObj.data.url,
            );
        } else if (resultTextObj.type == "canvas") {
            return null;
            // return handleCanvas(role, resultTextObj.data);
        } else if (resultTextObj.type == "template_bids") {
            return handleTemplateBidsResult(role, resultTextObj.data);
        } else if (resultTextObj.type === "chat_options") {
            return "Options Received";
        } else if (resultTextObj.type === "artifact") {
            return handleArtifact(role, resultTextObj.data, sendMessage);
        } else {
            return handleObjectData(role, resultTextObj.data);
        }
    } else {
        return handleObjectData(role, content);
    }
};

const handleProjectResult = (role, project) => {
    const projectUrl = project.url;
    const projectName = project.name || "New Project";
    return (
        <SummaryAndDetails
            summaryElement={
                <Link
                    href={projectUrl}
                    color={role == "user" ? "secondary" : "primary"}
                    target="_blank"
                >
                    {projectName}
                </Link>
            }
        />
    );
};

const handleBidResult = (role, bid) => {
    const bidUrl = bid.url;
    const bidName = bid.name || "New Bid";
    return (
        <SummaryAndDetails
            summaryElement={
                <Link
                    href={bidUrl}
                    color={role == "user" ? "secondary" : "primary"}
                    target="_blank"
                >
                    {bidName}
                </Link>
            }
        />
    );
};

const handleTemplateBidsResult = (role, bids) => {
    const bidUrls = bids.map((bid) => {
        const bidUrl = `/bids/${bid.id}`;
        const bidName = bid.name || "Un-named Template Bid";
        return (
            <div key={bid.id}>
                <Link href={bidUrl} target="_blank">
                    {bidName}
                </Link>
            </div>
        );
    });
    return (
        <SummaryAndDetails
            summaryElement={bidUrls}
            detailsElement={
                <JsonNavigator name={"Template Bids"} data={bids} level={0} />
            }
        />
    );
};

const handleClientResult = (role, client) => {
    const clientUrl = client.url;
    const clientName = client.name || "New Client";
    return (
        <SummaryAndDetails
            summaryElement={
                <Link
                    color={role == "user" ? "secondary" : "primary"}
                    href={clientUrl}
                    target="_blank"
                >
                    {clientName}
                </Link>
            }
        />
    );
};

const handleErrorResult = (role, resultData) => {
    return (
        <SummaryAndDetails
            summaryElement={
                <span style={{ color: "red" }}>An error occurred</span>
            }
            detailsElement={
                <JsonNavigator
                    name={"Error Details"}
                    data={resultData}
                    level={0}
                />
            }
        />
    );
};

const handleSummarizedObject = (role, detailsObj, summaryObj) => {
    return (
        <SummaryAndDetails
            detailsElement={
                <JsonNavigator
                    name={"Data"}
                    data={detailsObj}
                    level={0}
                    md_keys={["content"]}
                />
            }
        />
    );
};

const handleToolCall = (role, toolCallObject) => {
    const input = toolCallObject.input;
    const name = toolCallObject.name;
    return handleSummarizedObject(role, toolCallObject, name);
};

const handleFile = (role, data) => {
    if (!data.file) {
        return;
    }
    const fileUrl = data.file.url;
    const fileName = data.file.name || "New File";
    return (
        <Chip
            variant="outlined"
            component={"a"}
            clickable
            href={fileUrl}
            target="_blank"
            label={fileName}
        />
    );
};

const handleDocument = (role, document_name, document_url) => {
    const documentUrl = document_url;
    const documentName = document_name || "New Document";
    return (
        <Link href={documentUrl} target="_blank">
            {documentName}
        </Link>
    );
};

const handleCanvas = (role, canvasData) => {
    return <i>Got Canvas</i>;
};

const handleArtifact = (role, data, sendMessage) => {
    const artifact = data.artifact;
    return (
        <ArtifactRenderer
            title={artifact.title}
            description={artifact.description}
            html={artifact.html}
            onSendPrompt={(text) => sendMessage(text)}
        />
    );
};

const sendElicitationResponse = (threadId, data, resumeThread) => {
    // TODO: better error handling
    request
        .post("llm-elicitation-response", {
            thread_id: threadId,
            data: data,
        })
        .then(() => {
            resumeThread(threadId);
        })
        .catch((error) => {
            console.error("Error sending elicitation response:", error);
        });
};

const handleElicitationRequest = (role, content, threadId, resumeThread) => {
    const elicitationType = content.properties.type?.default;
    let component;
    if (elicitationType == "simple_confirmation") {
        component = (
            <ElicitationResponseBool
                onChoose={(data) => {
                    sendElicitationResponse(threadId, data, resumeThread);
                }}
            />
        );
    } else if (elicitationType == "bid_id_confirmation") {
        component = (
            <ElicitationResponseTemplateBidRadios
                onConfirm={(data) => {
                    sendElicitationResponse(threadId, data, resumeThread);
                }}
            />
        );
    }

    if (component) {
        return component;
    } else {
        console.log(content);
    }
};

const handleElicitationResponse = (role, content) => {
    const contentData = content.data;
    if (contentData.bid_id) {
        return <span>Bid ID: {contentData.bid_id}</span>;
    }
    return <span>{contentData.accepted ? "Yes" : "No"}</span>;
};
